'use client';

import { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProjectModalProps {
    project: {
        id: number;
        title: string;
        category: string;
        description: string;
        fullDescription: string;
        image: string;
        alt: string;
        liveUrl: string;
        metrics: {
            trafficIncrease: string;
            rankingImprovement: string;
            conversionBoost: string;
        };
        technologies: string[];
        timeline: string;
        client: string;
        challenges: string[];
        solutions: string[];
        results: string[];
        testimonial: {
            text: string;
            author: string;
            position: string;
            avatar: string;
            avatarAlt: string;
        };
    } | null;
    isOpen: boolean;
    onClose: () => void;
}

export default function ProjectModal({ project, isOpen, onClose }: ProjectModalProps) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !project) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="relative w-full max-w-5xl max-h-[90vh] bg-background rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 bg-white/90 hover:bg-white rounded-lg transition-colors duration-200 shadow-lg"
                    aria-label="Close modal"
                >
                    <Icon name="XMarkIcon" size={24} className="text-text-primary" />
                </button>

                <div className="overflow-y-auto max-h-[90vh] custom-scrollbar">
                    <div className="relative h-80">
                        <AppImage
                            src={project.image}
                            alt={project.alt}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                            <span className="inline-block px-3 py-1 bg-brand-accent text-brand-accent-foreground text-xs font-semibold rounded-full mb-3">
                                {project.category}
                            </span>
                            <h2 className="text-3xl font-bold text-white mb-2">{project.title}</h2>
                            <p className="text-white/90 text-sm">{project.client} • {project.timeline}</p>
                        </div>
                    </div>

                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                            <div className="text-center p-4 bg-success/10 border border-success/20 rounded-xl">
                                <div className="text-3xl font-bold text-success mb-1">{project.metrics.trafficIncrease}</div>
                                <div className="text-sm text-text-secondary">Traffic Increase</div>
                            </div>
                            <div className="text-center p-4 bg-brand-primary/10 border border-brand-primary/20 rounded-xl">
                                <div className="text-3xl font-bold text-brand-primary mb-1">{project.metrics.rankingImprovement}</div>
                                <div className="text-sm text-text-secondary">Ranking Improvement</div>
                            </div>
                            <div className="text-center p-4 bg-brand-accent/10 border border-brand-accent/20 rounded-xl">
                                <div className="text-3xl font-bold text-brand-accent mb-1">{project.metrics.conversionBoost}</div>
                                <div className="text-sm text-text-secondary">Conversion Boost</div>
                            </div>
                        </div>

                        <div className="mb-8">
                            <h3 className="text-xl font-bold text-text-primary mb-3">Project Overview</h3>
                            <p className="text-text-secondary leading-relaxed">{project.fullDescription}</p>
                        </div>

                        <div className="mb-8">
                            <h3 className="text-xl font-bold text-text-primary mb-3">Technologies Used</h3>
                            <div className="flex flex-wrap gap-2">
                                {project.technologies.map((tech, index) => (
                                    <span
                                        key={index}
                                        className="px-4 py-2 bg-secondary/10 text-secondary text-sm font-medium rounded-lg"
                                    >
                                        {tech}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <h3 className="text-xl font-bold text-text-primary mb-3">Challenges</h3>
                                <ul className="space-y-2">
                                    {project.challenges.map((challenge, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <Icon name="ExclamationTriangleIcon" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                                            <span className="text-text-secondary text-sm">{challenge}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-text-primary mb-3">Solutions</h3>
                                <ul className="space-y-2">
                                    {project.solutions.map((solution, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                                            <span className="text-text-secondary text-sm">{solution}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="mb-8">
                            <h3 className="text-xl font-bold text-text-primary mb-3">Key Results</h3>
                            <ul className="space-y-2">
                                {project.results.map((result, index) => (
                                    <li key={index} className="flex items-start gap-2">
                                        <Icon name="SparklesIcon" size={20} className="text-brand-accent flex-shrink-0 mt-0.5" />
                                        <span className="text-text-secondary text-sm">{result}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-muted rounded-xl p-6 mb-6">
                            <div className="flex items-start gap-4">
                                <AppImage
                                    src={project.testimonial.avatar}
                                    alt={project.testimonial.avatarAlt}
                                    className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                                />
                                <div>
                                    <p className="text-text-primary italic mb-3">"{project.testimonial.text}"</p>
                                    <div>
                                        <div className="font-semibold text-text-primary">{project.testimonial.author}</div>
                                        <div className="text-sm text-text-secondary">{project.testimonial.position}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <a
                            href={project.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-brand-primary-foreground font-semibold rounded-lg hover:bg-brand-primary/90 transition-colors duration-200"
                        >
                            <span>Visit Live Website</span>
                            <Icon name="ArrowTopRightOnSquareIcon" size={20} />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}